import type { AvailableCard } from '@/components/driver/available-order-card'
import type { SalesOrder } from '@/lib/api/sales-order-api'
import type { ContractDeliveryOrder } from '@/lib/api/contract-delivery-api'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Số đơn mỗi trang ở màn "Đơn chờ nhận" — cũng là per_page gọi API.
 *
 * Trên dữ liệu production, pool đơn chờ nhận có 2201 card — dựng hết một lượt
 * ra 14.294 node DOM và trang cao 262.512px CSS (≈311 màn điện thoại xếp
 * chồng). Tài xế cũng không bao giờ cuộn hết chừng đó; họ tìm đúng đơn của
 * mình. Nên: tải + dựng dần theo trang, kèm ô tìm kiếm (ở server) để nhảy
 * thẳng tới đơn cần.
 */
export const DRIVER_CARDS_PAGE_SIZE = 20

function toSaleCard(o: SalesOrder): AvailableCard {
    return {
        kind: 'sale',
        id: o.id,
        code: o.order_code,
        recipientName: o.customer_name,
        phone: o.customer_phone,
        address: o.shipping_address,
        amountLabel:
            o.payment_status === 'paid'
                ? formatVnd(o.total_amount)
                : `${m.driver_order_cod_label()}: ${formatVnd(o.total_amount)}`,
    }
}

function toContractCard(o: ContractDeliveryOrder): AvailableCard {
    return {
        kind: 'contract',
        id: o.id,
        code: o.order_code,
        recipientName: o.recipient_name,
        phone: o.recipient_phone,
        address: o.shipping_address,
    }
}

/** Bỏ đơn trùng id (trang sau lệch khi có đơn mới chen vào đầu danh sách) và
 *  đơn đã có tài xế — server vốn đã loại (nhận đơn là rời `stock_out` /
 *  `confirmed`), đây chỉ là lớp an toàn. */
function unclaimedUnique<
    T extends { id: string; driver_assignee_id?: string | null },
>(rows: T[]): T[] {
    const seen = new Set<string>()
    return rows.filter((r) => {
        if (r.driver_assignee_id || seen.has(r.id)) return false
        seen.add(r.id)
        return true
    })
}

/**
 * Ghép các trang đã tải của 2 nguồn thành danh sách card. Đơn giao hợp đồng
 * đứng ĐẦU: ít đơn nhưng dễ chìm dưới hàng nghìn đơn bán, tài xế không được bỏ
 * lỡ. Còn trang đơn hợp đồng thì chưa hiện đơn bán, tránh chen vào giữa.
 */
export function buildAvailableCards({
    contracts,
    contractsHasMore,
    sales,
}: {
    contracts: ContractDeliveryOrder[]
    contractsHasMore: boolean
    sales: SalesOrder[]
}): AvailableCard[] {
    const contractCards = unclaimedUnique(contracts).map(toContractCard)
    if (contractsHasMore) return contractCards
    return [...contractCards, ...unclaimedUnique(sales).map(toSaleCard)]
}
